import { Button } from '@column/column-ui-kit';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { RegisterAction, RegisterContent, RegisterContentGrid } from '../Questions';
import { Form } from '../../Login';
import { Address } from './Partial/Address';
import { Countries } from './Partial/Countries';
import { ROUTE } from '~/public/routes';
import { FormFields, FormLabel, FormElement, Headline, Paragraph } from '~/styles';
import { BusinessPageIdType, PlatformRootEntityCache } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { NotificationList } from '~/components';

export const FormElementHint = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  margin: -2px 0 4px 0;
`;

export const PageRegisterBusinessLocation: React.FC = () => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const currentPlatformState = useSessionStore((state) => state.currentPlatform);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [businessOperateCountries, setBusinessOperateCountries] = useState<string[]>(['']);
  const navigate = useNavigate();
  const useFormMethods = useForm<PlatformRootEntityCache>();

  const { handleSubmit, reset } = useFormMethods;

  const onSuccess = (rootEntityCache: PlatformRootEntityCache) => {
    if (isLoading) {
      return;
    }

    if (businessOperateCountries.filter(Boolean).length < 1) {
      addDangerNotification({
        content: 'Please select at least one country.',
        display: 'business-countries',
      });
      return;
    }

    setIsLoading(true);

    currentPlatformState
      ?.updateRootEntityCache({
        ...rootEntityCache,
        businessOperateCountries,
        completedPages: [
          ...new Set<BusinessPageIdType>([...(currentPlatformState.rootEntityCache?.completedPages ?? []), 'location']),
        ],
      })
      .then(() => {
        navigate(ROUTE.REGISTER_BUSINESS_CUSTOMER_TYPES);
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'page',
        });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => state.currentPlatform,
        (currentPlatform) => {
          reset(currentPlatform?.rootEntityCache);

          setBusinessOperateCountries(currentPlatform?.rootEntityCache?.businessOperateCountries ?? ['']);
        },
        { fireImmediately: true }
      ),
    []
  );

  return (
    <RegisterContent>
      <RegisterContentGrid>
        <Headline>Business location</Headline>
        <Paragraph size="small">
          This section has yet another set of questions we’re required to ask you, to understand if there is an
          international component to your business.
        </Paragraph>
      </RegisterContentGrid>
      <FormProvider {...useFormMethods}>
        <Form onSubmit={handleSubmit(onSuccess)}>
          <NotificationList display="page" />
          <RegisterContent>
            <div>
              <NotificationList display="business-countries" />
              <Countries
                value={businessOperateCountries}
                onValueChange={setBusinessOperateCountries}
                formLabel="Where does your business operate?"
              />
            </div>
            <FormFields>
              <FormElement>
                <FormLabel>What is your business's registered address?</FormLabel>
                <FormElementHint>
                  This will be the same address found on your state registration or Employer Identification Number
                  documents.
                </FormElementHint>
              </FormElement>
              <Address id="businessAddress" formLabel="" />
            </FormFields>
          </RegisterContent>
          <RegisterAction>
            <Button variant="muted" onClick={() => navigate(ROUTE.REGISTER_BUSINESS_INFORMATION)} type="button">
              Back
            </Button>
            <Button isLoading={isLoading} isDisabled={false}>
              Continue
            </Button>
          </RegisterAction>
        </Form>
      </FormProvider>
    </RegisterContent>
  );
};
